import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { parseDateInCST } from '../utils/dateUtils';
import { cleanText } from '../utils/textUtils';
import LocationDisplay from '../components/LocationDisplay';
import FavoriteButton from '../components/FavoriteButton';
import ShareButton from '../components/ShareButton';
import './CampsPage.css';
import './SharedCardStyles.css';

interface Camp { id: string; slug: string; name: string; short_description: string; camp_type: string; category: string; venue_name: string; address?: string; city: string; latitude?: number; longitude?: number; start_date: string; end_date: string; weeks_available: number; schedule?: string; price_per_week: number; price_description?: string; age_min: number; age_max: number; lunch_included: boolean; snacks_included: boolean; registration_url?: string; }

const CampsPage: React.FC = () => {
  const navigate = useNavigate();
  const [camps, setCamps] = useState<Camp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { fetchCamps(); }, []);

  const fetchCamps = async () => {
    try {
      setLoading(true); setError(null);
      const today = new Date().toISOString().split('T')[0];
      let query = supabase.from('camps').select('*').eq('status', 'active').gte('end_date', today);
      const selectedCity = localStorage.getItem('selectedCity');
      if (selectedCity) query = query.eq('city', selectedCity);
      const { data, error } = await query.order('start_date', { ascending: true });
      if (error) throw error;
      setCamps(data || []);
    } catch (err: any) { setError(err.message || 'Failed to load camps'); } finally { setLoading(false); }
  };

  const formatDate = (dateString: string) => parseDateInCST(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Chicago' });
  const handleWebsite = (url: string | undefined) => { if (url) window.open(url, '_blank', 'noopener,noreferrer'); };

  if (loading) return <div className="camps-container"><div className="loading"><div className="spinner"></div><p>Loading camps...</p></div></div>;
  if (error) return <div className="camps-container"><div className="error"><p>😕 Oops! {error}</p><button onClick={fetchCamps} className="retry-btn">Try Again</button></div></div>;

  return (
    <div className="camps-container">
      <button className="back-button" onClick={() => navigate('/')}>← Back to Home</button>
      <LocationDisplay />
      <div className="camps-header"><h1>🏕️ Camps</h1><p>Winter, spring & summer break programs</p></div>
      {camps.length === 0 ? <div className="no-camps"><p>🏕️ No camps found. Check back soon!</p></div> : (
        <div className="camps-list">
          {camps.filter(camp => camp.id && camp.id !== 'null' && camp.id !== 'undefined').map(camp => (
            <div key={camp.id} className="camp-card">
              <div className="favorite-button-wrapper"><FavoriteButton itemId={camp.id} itemType="camp" size="small" /></div>
              <div className="camp-header-badge"><span className="camp-category">{camp.category}</span><span className="camp-type">{camp.camp_type}</span></div>
              <div className="camp-content">
                <h3 className="camp-name">{camp.name}</h3>
                <p className="camp-venue-name">{camp.venue_name}</p>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    camp.address 
                      ? `${camp.venue_name} ${camp.address}, ${camp.city}`.trim()
                      : `${camp.venue_name}, ${camp.city}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="location-link camp-address"
                  onClick={(e) => e.stopPropagation()}
                >
                  📍 {camp.address || camp.city}
                </a>
                <p className="camp-description">{cleanText(camp.short_description)}</p>
                <div className="camp-details">
                  <div className="detail"><span className="detail-icon">📅</span><span>{formatDate(camp.start_date)} - {formatDate(camp.end_date)}</span></div>
                  <div className="detail"><span className="detail-icon">🗓️</span><span>{camp.weeks_available} weeks available</span></div>
                  <div className="detail"><span className="detail-icon">👶</span><span>Ages {camp.age_min}-{camp.age_max}</span></div>
                  <div className="detail"><span className="detail-icon">💰</span><span>${camp.price_per_week}/week</span></div>
                  {camp.schedule && <div className="detail"><span className="detail-icon">⏰</span><span>{camp.schedule}</span></div>}
                </div>
                <div className="camp-features">
                  {camp.lunch_included && <span className="feature-badge">🍱 Lunch Included</span>}
                  {camp.snacks_included && <span className="feature-badge">🍪 Snacks Included</span>}
                </div>
                <div className="camp-actions">
                  <button 
                    className={`action-button icon-only ${!camp.registration_url ? 'disabled' : ''}`}
                    onClick={() => handleWebsite(camp.registration_url)}
                    disabled={!camp.registration_url}
                    title="Visit Website"
                    aria-label="Visit Website"
                  >
                    🌐
                  </button>
                  <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                    <ShareButton 
                      title={camp.name}
                      description={camp.short_description}
                      url={`${window.location.origin}/camps`}
                      date={`${formatDate(camp.start_date)} - ${formatDate(camp.end_date)}`}
                      location={`${camp.venue_name}, ${camp.city}`}
                      size="small"
                      showLabel={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampsPage;
